import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useAuthStore } from '@carrent/core'
import {
  mergeFeed,
  messageTimeLabel,
  ownMessageStatus,
  useConversation,
} from '@carrent/domain'
import { offline } from '@carrent/platform'
import { Icon, MessageBubble, MessageComposer, PinnedBar } from '@carrent/ui'

import {
  cacheConversation,
  cachedConversation,
  enqueueChatMessage,
  queuedMessages,
} from '../../../offline/chatTasks'

/**
 * Лента диалога — экран, ради которого делалась офлайн-очередь.
 *
 * Порядок отрисовки: кэш → сеть → события. Кэш показывается сразу
 * (водитель в туннеле перечитывает распоряжение), сеть заменяет его,
 * события добавляются поверх. Свои неотправленные сообщения
 * подмешиваются из очереди и исчезают из неё, как только сервер
 * подтвердил (mergeFeed по client_message_id).
 *
 * Одно основное действие на экран — отправка. Вложения — из шторки.
 */
export default function ChatPage() {
  const { id } = useParams()
  const user = useAuthStore((s) => s.user)
  const {
    messages, pinned, details, loading, error,
    hasMore, loadOlder, appendLocal, markRead, reload,
  } = useConversation(id)

  const [queued, setQueued] = useState([])
  const [cached, setCached] = useState(null)
  const [attachOpen, setAttachOpen] = useState(false)
  const bottomRef = useRef(null)
  const photoInputRef = useRef(null)
  const docInputRef = useRef(null)

  // ── Кэш чтения ─────────────────────────────────────────────
  useEffect(() => {
    let alive = true
    cachedConversation(id).then((items) => {
      if (alive && items) setCached(items)
    })
    return () => {
      alive = false
    }
  }, [id])

  useEffect(() => {
    if (messages.length) cacheConversation(id, messages)
  }, [id, messages])

  // ── Очередь устройства ─────────────────────────────────────
  const refreshQueue = useCallback(() => {
    queuedMessages(id).then(setQueued)
  }, [id])

  useEffect(() => {
    refreshQueue()
    return offline.worker.subscribe(refreshQueue)
  }, [refreshQueue])

  // ── Лента ──────────────────────────────────────────────────
  const feed = useMemo(() => {
    const serverFeed = messages.length ? messages : (loading && cached) || []
    return mergeFeed(serverFeed, queued)
  }, [messages, loading, cached, queued])

  // Автопрокрутка к последнему и отметка прочтения.
  const lastServerId = messages.length ? messages[messages.length - 1].id : null
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' })
    if (lastServerId) markRead(lastServerId)
  }, [feed.length, lastServerId, markRead])

  // ── Отправка ───────────────────────────────────────────────
  const send = useCallback(
    async ({ body }) => {
      const { message } = await enqueueChatMessage({ conversationId: id, body })
      appendLocal(message)
      refreshQueue()
      offline.worker.flush() // при живой сети уйдёт сразу
    },
    [id, appendLocal, refreshQueue]
  )

  const sendFiles = useCallback(
    async (files, asDocument) => {
      if (!files?.length) return
      setAttachOpen(false)
      const { message } = await enqueueChatMessage({
        conversationId: id,
        body: '',
        files: Array.from(files),
        asDocument,
      })
      appendLocal(message)
      refreshQueue()
      offline.worker.flush()
    },
    [id, appendLocal, refreshQueue]
  )

  const retry = useCallback(
    async (taskId) => {
      await offline.outbox.retry(taskId)
      refreshQueue()
      offline.worker.flush()
    },
    [refreshQueue]
  )

  const title =
    details?.kind === 'group' ? details?.title || 'Группа' : details?.title || 'Диалог'

  return (
    <div className="chat-screen">
      <header className="chat-screen__header">
        <span className="chat-screen__title">{title}</span>
        {details?.members_count > 2 && (
          <span className="chat-screen__count text-muted">
            {details.members_count} участников
          </span>
        )}
      </header>

      <PinnedBar pinned={pinned[0]} />

      <div className="chat-screen__feed">
        {loading && !cached && feed.length === 0 && (
          <div className="chat-screen__state">
            <span className="spinner" aria-label="Загрузка" />
          </div>
        )}

        {!loading && error && feed.length === 0 && (
          <div className="chat-screen__state">
            <p className="text-muted">{error}</p>
            <button type="button" className="btn btn--secondary" onClick={reload}>
              Повторить
            </button>
          </div>
        )}

        {!loading && !error && feed.length === 0 && (
          <div className="chat-screen__state">
            <Icon name="chat" size={36} />
            <p className="text-muted">Сообщений пока нет — напишите первое.</p>
          </div>
        )}

        {hasMore && (
          <button
            type="button"
            className="btn btn--ghost chat-screen__older"
            onClick={loadOlder}
          >
            Показать более ранние
          </button>
        )}

        {feed.map((m) => {
          const own = m.queued || m.sender_id === user?.id
          return (
            <MessageBubble
              key={m.client_message_id || m.id}
              message={m}
              own={own}
              senderName={!own ? m.sender?.full_name : ''}
              timeLabel={messageTimeLabel(m.created_at)}
              status={own ? ownMessageStatus(m, details?.members, user?.id) : null}
              onRetry={m.failed && m.taskId ? () => retry(m.taskId) : undefined}
            />
          )
        })}
        <div ref={bottomRef} />
      </div>

      <MessageComposer
        onSend={send}
        onAttach={() => setAttachOpen(true)}
        placeholder="Сообщение диспетчеру"
      />

      {attachOpen && (
        <div
          className="sheet-overlay"
          role="presentation"
          onClick={() => setAttachOpen(false)}
        >
          <div className="sheet" role="dialog" aria-label="Прикрепить">
            <button
              type="button"
              className="sheet__item"
              onClick={() => photoInputRef.current?.click()}
            >
              <Icon name="camera" size={22} />
              <span>
                Фото
                <small className="text-muted">Поломка, место, обстановка</small>
              </span>
            </button>
            <button
              type="button"
              className="sheet__item"
              onClick={() => docInputRef.current?.click()}
            >
              <Icon name="file" size={22} />
              <span>
                Документ
                <small className="text-muted">Накладная, ТОРГ-2 — сжатие мягче</small>
              </span>
            </button>
            <button
              type="button"
              className="sheet__item sheet__item--cancel"
              onClick={() => setAttachOpen(false)}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Скрытые input'ы: платформенная камера — это file+capture. */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={(e) => sendFiles(e.target.files, false)}
      />
      <input
        ref={docInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => sendFiles(e.target.files, true)}
      />
    </div>
  )
}
